// src/components/preferences/CompanyPreferenceEditor.tsx
/**
 * Editor for company preferences (stages, sizes, values)
 * ⚠️ FIXED: Uses correct field names matching backend schema
 */
import React from 'react';
import {
  IonItem,
  IonLabel,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonList,
  IonChip,
  IonIcon,
  IonButton,
} from '@ionic/react';
import { closeCircle } from 'ionicons/icons';
import { CompanyPreferences, COMPANY_STAGES, COMPANY_SIZES } from '../../types/preferences.ts';
import './PreferenceEditor.css';

interface CompanyPreferenceEditorProps {
  data: CompanyPreferences;
  onChange: (data: CompanyPreferences) => void;
}

const CompanyPreferenceEditor: React.FC<CompanyPreferenceEditorProps> = ({ data, onChange }) => {
  const [newValue, setNewValue] = React.useState('');

  const handleFieldChange = (field: keyof CompanyPreferences, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const handleAddValue = () => {
    if (newValue.trim()) {
      const currentList = (data.values || []) as string[];
      onChange({
        ...data,
        values: [...currentList, newValue.trim()],
      });
      setNewValue('');
    }
  };

  const handleRemoveValue = (index: number) => {
    const currentList = (data.values || []) as string[];
    onChange({
      ...data,
      values: currentList.filter((_, i) => i !== index),
    });
  };

  return (
    <IonList className="preference-editor-list">
      <IonItem>
        <IonLabel position="stacked">Company Stages</IonLabel>
        <IonSelect
          value={data.stages || []}
          placeholder="Select company stages"
          multiple={true}
          onIonChange={(e) => handleFieldChange('stages', e.detail.value)}
        >
          {COMPANY_STAGES.map((stage) => (
            <IonSelectOption key={stage} value={stage}>
              {stage}
            </IonSelectOption>
          ))}
        </IonSelect>
      </IonItem>

      <IonItem>
        <IonLabel position="stacked">Company Sizes</IonLabel>
        <IonSelect
          value={data.sizes || []}
          placeholder="Select company sizes"
          multiple={true}
          onIonChange={(e) => handleFieldChange('sizes', e.detail.value)}
        >
          {COMPANY_SIZES.map((size) => (
            <IonSelectOption key={size} value={size}>
              {size} employees
            </IonSelectOption>
          ))}
        </IonSelect>
      </IonItem>

      <IonItem>
        <IonLabel position="stacked">Company Values</IonLabel>
        <div className="chip-input-container">
          {data.values && data.values.length > 0 && (
            <div className="chip-list">
              {data.values.map((value, index) => (
                <IonChip key={index} color="primary">
                  {value}
                  <IonIcon
                    icon={closeCircle}
                    onClick={() => handleRemoveValue(index)}
                  />
                </IonChip>
              ))}
            </div>
          )}
          <div className="chip-input">
            <IonInput
              value={newValue}
              placeholder="e.g., Innovation, Work-life balance"
              onIonChange={(e) => setNewValue(e.detail.value || '')}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddValue();
                }
              }}
            />
            <IonButton onClick={handleAddValue} size="small">
              Add
            </IonButton>
          </div>
        </div>
      </IonItem>
    </IonList>
  );
};

export default CompanyPreferenceEditor;
